import { useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { TrendingUp, TrendingDown, Minus, Home, Sparkles, Loader2, AlertTriangle } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, CartesianGrid } from "recharts";

const DIR_ICON = { up: TrendingUp, down: TrendingDown, neutral: Minus };
const DIR_TONE = {
  up: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
  down: "text-rose-300 border-rose-500/40 bg-rose-500/10",
  neutral: "text-zinc-300 border-zinc-500/40 bg-zinc-500/10",
};

const money = (n) => n == null ? "—" : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function RealEstatePredictorPage() {
  const { currentProperty } = useProperty();
  const [zip, setZip] = useState("");
  const [propType, setPropType] = useState("single_family");
  const [sqft, setSqft] = useState("");
  const [beds, setBeds] = useState("");
  const [baths, setBaths] = useState("");
  const [currentVal, setCurrentVal] = useState("");
  const [macro, setMacro] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const analyze = async () => {
    if (!zip) return toast.error("Enter a ZIP code");
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/admin/ai/real-estate/forecast", {
        zip_code: zip,
        community_name: currentProperty?.name || null,
        property_type: propType,
        average_sqft: sqft ? Number(sqft) : null,
        beds: beds ? Number(beds) : null,
        baths: baths ? Number(baths) : null,
        current_median_value: currentVal ? Number(currentVal) : null,
        macro_notes: macro || null,
      });
      setResult(data);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setBusy(false);
  };

  const chart = result ? [
    { label: "Today", value: result.median_value_now || Number(currentVal) || null },
    { label: "6 mo", value: result.forecast_6mo?.median },
    { label: "12 mo", value: result.forecast_12mo?.median },
    { label: "24 mo", value: result.forecast_24mo?.median },
  ].filter((p) => p.value != null) : [];

  return (
    <div>
      <PageHeader eyebrow="AI Features" title="Real Estate Predictor" subtitle="Directional forecast using macro trends + your community's attributes. Not investment advice." />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="border-white/10 bg-card p-5 lg:col-span-1" data-testid="re-form">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-9 w-9 rounded-md bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
              <Home className="h-4 w-4 text-emerald-300" />
            </div>
            <div className="text-lg font-semibold font-display">Community inputs</div>
          </div>
          <div className="space-y-3">
            <div>
              <Label>ZIP code</Label>
              <Input value={zip} onChange={(e) => setZip(e.target.value)} placeholder="90210" data-testid="re-zip" className="mt-1 bg-white/[0.02] border-white/10" />
            </div>
            <div>
              <Label>Property type</Label>
              <Select value={propType} onValueChange={setPropType}>
                <SelectTrigger data-testid="re-type" className="mt-1 bg-white/[0.02] border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="single_family">Single family</SelectItem>
                  <SelectItem value="condo">Condo</SelectItem>
                  <SelectItem value="townhome">Townhome</SelectItem>
                  <SelectItem value="multi_family">Multi-family</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs">Sqft</Label>
                <Input type="number" value={sqft} onChange={(e) => setSqft(e.target.value)} data-testid="re-sqft" className="mt-1 bg-white/[0.02] border-white/10" />
              </div>
              <div>
                <Label className="text-xs">Beds</Label>
                <Input type="number" value={beds} onChange={(e) => setBeds(e.target.value)} data-testid="re-beds" className="mt-1 bg-white/[0.02] border-white/10" />
              </div>
              <div>
                <Label className="text-xs">Baths</Label>
                <Input type="number" step="0.5" value={baths} onChange={(e) => setBaths(e.target.value)} data-testid="re-baths" className="mt-1 bg-white/[0.02] border-white/10" />
              </div>
            </div>
            <div>
              <Label>Current median value (USD, optional)</Label>
              <Input type="number" value={currentVal} onChange={(e) => setCurrentVal(e.target.value)} placeholder="850000" data-testid="re-value" className="mt-1 bg-white/[0.02] border-white/10" />
            </div>
            <div>
              <Label>Macro context (optional)</Label>
              <Textarea rows={3} value={macro} onChange={(e) => setMacro(e.target.value)} placeholder="Local employer news, planned infrastructure, recent zoning changes…" data-testid="re-macro" className="mt-1 bg-white/[0.02] border-white/10" />
            </div>
            <Button onClick={analyze} disabled={busy} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="re-analyze">
              {busy ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Forecasting…</> : <><Sparkles className="h-3 w-3 mr-1" /> Forecast</>}
            </Button>
          </div>
        </Card>

        <Card className="border-white/10 bg-card p-5 lg:col-span-2" data-testid="re-result">
          {!result && !busy && (
            <div className="text-center py-16 text-sm text-zinc-500" data-testid="re-empty">
              Enter a ZIP and hit Forecast. Claude runs a macro-driven directional forecast for 6, 12, and 24 months.
            </div>
          )}
          {busy && <div className="text-center py-16 text-sm text-zinc-400 flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Claude is analyzing macro signals…</div>}
          {result && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {["6mo", "12mo", "24mo"].map((k) => {
                  const f = result[`forecast_${k}`];
                  return (
                    <div key={k} className="rounded-md border border-white/10 bg-white/[0.02] p-3" data-testid={`re-forecast-${k}`}>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{k.replace("mo", " months")}</div>
                      <div className="text-lg font-semibold tabular-nums mt-1">{money(f?.median)}</div>
                      <div className="text-[10px] text-zinc-500 mt-0.5">{money(f?.low)} – {money(f?.high)}</div>
                    </div>
                  );
                })}
              </div>

              {chart.length > 1 && (
                <div className="h-56" data-testid="re-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chart}>
                      <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                      <XAxis dataKey="label" stroke="#71717a" fontSize={11} />
                      <YAxis stroke="#71717a" fontSize={11} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                      <Tooltip contentStyle={{ background: "#0a0a0b", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} formatter={(v) => money(v)} />
                      <Line type="monotone" dataKey="value" stroke="#10b981" strokeWidth={2} dot={{ fill: "#10b981", r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {result.market_signals?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Market signals</div>
                  <div className="flex flex-wrap gap-2">
                    {result.market_signals.map((s, i) => {
                      const Ic = DIR_ICON[s.direction] || Minus;
                      return (
                        <Badge key={i} variant="outline" className={DIR_TONE[s.direction] + " gap-1"} data-testid={`re-signal-${i}`}>
                          <Ic className="h-3 w-3" /> {s.signal} · {s.confidence}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {result.top_risks?.length > 0 && (
                  <div className="rounded-md border border-rose-500/20 bg-rose-500/[0.03] p-3" data-testid="re-risks">
                    <div className="text-[10px] uppercase tracking-widest text-rose-300 mb-1">Top risks</div>
                    <ul className="text-xs text-zinc-300 space-y-1 list-disc list-inside">
                      {result.top_risks.map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                  </div>
                )}
                {result.top_tailwinds?.length > 0 && (
                  <div className="rounded-md border border-emerald-500/20 bg-emerald-500/[0.03] p-3" data-testid="re-tailwinds">
                    <div className="text-[10px] uppercase tracking-widest text-emerald-300 mb-1">Tailwinds</div>
                    <ul className="text-xs text-zinc-300 space-y-1 list-disc list-inside">
                      {result.top_tailwinds.map((t, i) => <li key={i}>{t}</li>)}
                    </ul>
                  </div>
                )}
              </div>

              {result.hoa_action_items?.length > 0 && (
                <div className="rounded-md border border-indigo-500/20 bg-indigo-500/[0.03] p-3" data-testid="re-actions">
                  <div className="text-[10px] uppercase tracking-widest text-indigo-300 mb-1">HOA action items</div>
                  <ul className="text-xs text-zinc-300 space-y-1 list-disc list-inside">
                    {result.hoa_action_items.map((a, i) => <li key={i}>{a}</li>)}
                  </ul>
                </div>
              )}

              {result.disclaimer && (
                <div className="text-[10px] text-zinc-500 border-t border-white/5 pt-3 flex items-start gap-1" data-testid="re-disclaimer">
                  <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" />
                  <div>{result.disclaimer}</div>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
